import React, { useState, useMemo, useEffect } from 'react';
import StreamingText from './StreamingText';
import MarkdownRenderer from './MarkdownRenderer';
import PDFViewerModal from './PDFViewerModal';
import { DocumentResponse } from '../services/apiService';
import { processCitations, removeDocumentCitations } from '../utils/citationProcessor';
import { StreamingCitationProcessor, removeDocumentCitationsStreaming } from '../utils/streamingCitationProcessor';

interface LocalMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  documents?: DocumentResponse[];
}

interface ChatMessageProps {
  message: LocalMessage;
  isStreaming?: boolean;
  onCanvasOpen?: (content: string) => void;
  citationProcessingEnabled?: boolean;
}

export default React.memo(function ChatMessage({ message, isStreaming = false, onCanvasOpen, citationProcessingEnabled = true }: ChatMessageProps) {
  const [showDocuments, setShowDocuments] = useState(false);
  const [activeDocumentTab, setActiveDocumentTab] = useState<'overview' | 'content'>('overview');
  const [copySuccess, setCopySuccess] = useState(false);
  const [documentsReceived, setDocumentsReceived] = useState(false);
  
  // PDF modal state
  const [pdfModal, setPdfModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({ isOpen: false, url: '', title: '' });
  
  // Streaming citation processor for real-time processing
  const streamingProcessor = useMemo(() => {
    if (message.role === 'assistant' && message.documents && citationProcessingEnabled) {
      return new StreamingCitationProcessor(message.documents);
    }
    return null;
  }, [message.role, message.documents, citationProcessingEnabled]);
  
  // Note: Removed real-time streaming citation processing to prevent browser freezing
  // Citations are now only processed after streaming is complete

  // Process citations when streaming is complete and citation processing is enabled
  const citationData = useMemo(() => {
    if (message.role === 'assistant' && !isStreaming && message.content && message.documents && citationProcessingEnabled) {
      return processCitations(message.content, message.documents);
    }
    return {
      processedContent: message.content,
      documentMap: new Map(),
      referencedDocuments: message.documents || []
    };
  }, [message.content, message.documents, message.role, isStreaming, citationProcessingEnabled]);

  // Force re-render when streaming completes to trigger citation processing
  const [streamingCompleted, setStreamingCompleted] = useState(false);
  
  useEffect(() => {
    if (!isStreaming && message.content && !streamingCompleted) {
      setStreamingCompleted(true);
      // Small delay to ensure the component re-renders with processed citations
      setTimeout(() => {
        setStreamingCompleted(false);
      }, 100);
    }
  }, [isStreaming, message.content, streamingCompleted]);

  // Track when documents are received
  useEffect(() => {
    if (message.documents && message.documents.length > 0 && !documentsReceived) {
      setDocumentsReceived(true);
    }
  }, [message.documents, documentsReceived]);

  // PDF modal handlers
  const openPDFModal = (url: string, title: string) => {
    setPdfModal({ isOpen: true, url, title });
  };

  const closePDFModal = () => {
    setPdfModal({ isOpen: false, url: '', title: '' });
  };

  // Handle document click - open PDF if available
  const handleDocumentClick = (doc: DocumentResponse) => {
    if (doc.file) {
      // For PDF documents from our API
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL}/documents/${doc.file}`;
      openPDFModal(pdfUrl, doc.headline || doc.document);
    } else if (doc.source_url) {
      // For external URLs, open in new tab
      window.open(doc.source_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Copy message content to clipboard with rich text formatting (mimics user selection)
  const handleCopy = async () => {
    try {
      // Find the rendered markdown content element (inside MarkdownRenderer)
      const markdownContentElement = document.querySelector(`[data-message-id="${message.id}"] .markdown-content`);
      
      if (markdownContentElement) {
        // Clone the element to avoid modifying the original
        const clonedElement = markdownContentElement.cloneNode(true) as HTMLElement;
        
        // Remove tooltips and other invisible/unwanted elements
        const unwantedSelectors = [
          'span[class*="tooltip"]',
          'span[class*="absolute"]',
          'span[class*="opacity-0"]',
          'span[class*="pointer-events-none"]',
          '.tooltip',
          '[style*="position: absolute"]',
          '[style*="opacity: 0"]',
          '[style*="display: none"]'
        ];
        
        unwantedSelectors.forEach(selector => {
          const elements = clonedElement.querySelectorAll(selector);
          elements.forEach(el => el.remove());
        });
        
        // Create a temporary container for the cleaned content
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.appendChild(clonedElement);
        document.body.appendChild(tempContainer);
        
        // Create a range and select the cleaned content
        const range = document.createRange();
        range.selectNodeContents(clonedElement);
        
        // Get the current selection and replace it with our range
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        
        // Use the browser's native copy command (this preserves formatting like manual copy)
        const success = document.execCommand('copy');
        
        // Clean up
        document.body.removeChild(tempContainer);
        selection?.removeAllRanges();
        
        if (success) {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        } else {
          throw new Error('execCommand failed');
        }
      } else {
        // Fallback: copy raw message content
        await navigator.clipboard.writeText(message.content);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy formatted text: ', err);
      // Final fallback - copy plain text
      try {
        await navigator.clipboard.writeText(message.content);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error('All copy methods failed: ', fallbackErr);
      }
    }
  };

  // Render message content with citations and canvas support
  const renderMessageContent = () => {
    if (message.role === 'user') {
      return message.content;
    }

    let content = message.content || '';
    
    // Handle different states:
    if (!isStreaming && citationProcessingEnabled && citationData.processedContent) {
      // When Smart Citations enabled and streaming complete: Use processed content with clickable citations
      content = citationData.processedContent;
    } else if (!isStreaming && !citationProcessingEnabled) {
      // When Smart Citations disabled and streaming complete: Show raw response with [Document X, ...]
      content = message.content || '';
    } else {
      // During streaming: Always use original content (StreamingText will handle citation removal)
      content = message.content || '';
    }
    
    // Check if message starts with [canvas]
    if (content.startsWith('[canvas]')) {
      const canvasContent = content.replace('[canvas]', '').trim();
      
      return (
        <div>
          <div className="mb-3">
            <button
              onClick={() => onCanvasOpen?.(canvasContent)}
              className="inline-flex items-center px-3 py-2 theme-primary-light theme-primary-text rounded-lg hover:opacity-80 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Open Canvas
            </button>
          </div>
          <StreamingText content={canvasContent} isStreaming={isStreaming} />
        </div>
      );
    }

    return (
      <StreamingText 
        content={content} 
        isStreaming={isStreaming}
        streamingProcessor={streamingProcessor}
        citationProcessingEnabled={citationProcessingEnabled}
      />
    );
  };

  const renderLoadingIndicator = () => (
    <div className="flex items-center space-x-2 theme-text-muted typing-indicator">
      <div className="flex space-x-1">
        <div className="w-2 h-2 theme-primary rounded-full animate-bounce"></div>
        <div className="w-2 h-2 theme-primary rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
        <div className="w-2 h-2 theme-primary rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
      </div>
      <span className="text-sm fade-in-word">Thinking...</span>
    </div>
  );

  return (
    <div className="mb-6">
      <div 
        className={`mb-3 pl-2 ${
          message.role === 'user' 
            ? 'flex justify-end' 
            : 'flex justify-start'
        }`}
      >
        {message.role === 'assistant' && (
          <div className="w-10 h-10 hidden rounded-full theme-primary  items-center justify-center mr-3 mt-1 shadow-sm text-white">
            {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 theme-text-inverse" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
            </svg> */}
            AI
          </div>
        )}
        
        <div 
          className={`max-w-[90%] lg:max-w-[80%] rounded-3xl shadow-sm fade-in-message ${
            message.role === 'user' 
              ? 'theme-primary theme-text-inverse text-sm relative' 
              : 'theme-bg-secondary theme-text border theme-border min-w-[40%] '
          }`}
        >
          {message.role === 'assistant' ? (
            <div>
              {/* Main message content */}
              <div className="px-4 lg:px-5 py-3">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1 text-sm mb-2 lg:mb-0 message-content" data-message-id={message.id}>
                    {message.content
                      ? renderMessageContent()
                      : (isStreaming ? renderLoadingIndicator() : '')
                    }
                  </div>
                  {message.content && !isStreaming && (
                    <button
                      onClick={handleCopy}
                      className="self-start lg:ml-3 px-2 py-1 theme-text-secondary hover:theme-text rounded-lg hover:theme-bg-primary transition-colors flex-shrink-0 flex items-center space-x-1 text-xs"
                    >
                      {copySuccess ? (
                        <>
                          <svg className="w-3 h-3 theme-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="theme-success">Copied</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Document Search Status - Only during streaming */}
              {message.role === 'assistant' && isStreaming && (
                <div className="border-t theme-border">
                  <div className="px-4 lg:px-5 py-3">
                    {/* Document search status - looking for documents */}
                    {!documentsReceived && (
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1 !hidden">
                          <div className="w-2 h-2 theme-primary rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 theme-primary rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-2 h-2 theme-primary rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                        <span className="text-xs theme-text-secondary animate-pulse">Looking for relevant documents...</span>
                      </div>
                    )}
                    
                    {/* Documents found during streaming */}
                    {documentsReceived && message.documents && message.documents.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 theme-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs theme-success font-medium">Context retrieved, responding...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* References Section - Only when streaming is complete and Smart Citations enabled */}
              {message.role === 'assistant' && citationProcessingEnabled && citationData.referencedDocuments.length > 0 && !isStreaming && (
                <div className="border-t theme-border">
                  <div className="px-4 lg:px-5 py-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 theme-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xs font-medium theme-text-secondary">Used {citationData.referencedDocuments.length} references for this response</span>
                      </div>
                      <button
                        onClick={() => setShowDocuments(!showDocuments)}
                        className="text-xs theme-primary-text hover:opacity-80 transition-colors"
                      >
                        {showDocuments ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    
                        {showDocuments && (
                          <div>
                            {/* Tab Navigation */}
                            <div className="flex space-x-1 mb-3 theme-bg-primary rounded-lg p-1">
                              {/* <button
                                onClick={() => setActiveDocumentTab('overview')}
                                className={`px-3 py-2 text-xs rounded-md transition-colors flex-1 lg:flex-none ${
                                  activeDocumentTab === 'overview'
                                    ? 'theme-primary theme-text-inverse'
                                    : 'theme-text-secondary hover:theme-text'
                                }`}
                              >
                                Overview
                              </button> */}
                              {/* <button
                                onClick={() => setActiveDocumentTab('content')}
                                className={`px-3 py-2 text-xs rounded-md transition-colors flex-1 lg:flex-none ${
                                  activeDocumentTab === 'content'
                                    ? 'theme-primary theme-text-inverse'
                                    : 'theme-text-secondary hover:theme-text'
                                }`}
                              >
                                Full Content
                              </button> */}
                            </div>
                            
                            {/* Tab Content */}
                            <div className="max-h-52 lg:max-h-80 overflow-y-auto">
                              {activeDocumentTab === 'overview' ? (
                                <div className="space-y-0">
                                  {citationData.referencedDocuments.map((doc, index) => (
                                    <div 
                                      key={index} 
                                      id={`doc-${index + 1}`} 
                                      className={`flex items-start gap-1 p-0 theme-bg-primary group rounded-md hover:theme-bg-secondary transition-colors ${
                                        doc.file || doc.source_url ? 'cursor-pointer' : ''
                                      }`}
                                      onClick={() => handleDocumentClick(doc)}
                                    >
                                      <span className="inline-flex items-center justify-center w-4 h-4 text-center text-[11px] theme-primary-light theme-primary-text rounded-full  font-bold flex-shrink-0 mt-0.5">
                                        {index + 1}
                                      </span>
                                      <div className="flex items-center min-w-0 self-center pt-0.5 ">
                                        <div className="text-xs  hover:theme-primary-text font-medium theme-text truncate mb-0 self-center items-center ml-1  transition-colors">
                                          {doc.headline || doc.document}
                                          {/* {doc.file && (
                                            <span className="ml-1 text-xs theme-text-muted">(PDF)</span>
                                          )} */}
                                        </div>
                                        <div className="text-xs hover:theme-primary-text flex items-center gap-2 self-center">

                                          {!doc.file && (
                                            <span className="truncate ml-2 italic">({doc.source})</span>
                                          )}

                                          {(doc.source_url || doc.file) && (
                                            <span className="theme-primary-text flex-shrink-0 ml-2 hover:opacity-80 transition-colors">
                                              ↗
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {citationData.referencedDocuments.map((doc, index) => (
                                    <div key={index} id={`doc-${index + 1}`} className="border-b theme-border pb-3 last:border-b-0">
                                      <div className="text-xs font-medium theme-text mb-1">
                                        <span className="inline-block w-6 h-6 text-center theme-primary-light theme-primary-text rounded-full text-xs leading-6 mr-2 font-bold">
                                          {index + 1}
                                        </span>
                                        {doc.document}
                                      </div>
                                      <div className="text-xs theme-text-secondary mb-2 ml-8">
                                        {doc.stage && <span className="inline-block mr-2">Stage: {doc.stage}</span>}
                                        <span className="inline-block mr-2">Jurisdiction: {doc.jurisdiction}</span>
                                        <span className="inline-block">Date: {doc.date}</span>
                                      </div>
                                      <div className="text-xs theme-text-secondary leading-relaxed ml-8">
                                        <MarkdownRenderer>{doc.content}</MarkdownRenderer>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                  </div>
                </div>
              )}
              
              {/* Fallback: Show all documents when Smart Citations is disabled and streaming is complete */}
              {message.role === 'assistant' && message.documents && message.documents.length > 0 && !citationProcessingEnabled && !isStreaming && (
                <div className="border-t theme-border">
                  <div className="px-4 lg:px-5 py-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 theme-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xs font-medium theme-text-secondary">📚 Sources ({message.documents.length})</span>
                      </div>
                      <button
                        onClick={() => setShowDocuments(!showDocuments)}
                        className="text-xs theme-primary-text hover:opacity-80 transition-colors"
                      >
                        {showDocuments ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    
                    {showDocuments && (
                      <div>
                        {/* Tab Navigation */}
                        <div className="flex space-x-1 mb-3 theme-bg-primary rounded-lg p-1">
                          <button
                            onClick={() => setActiveDocumentTab('overview')}
                            className={`px-3 py-2 text-xs rounded-md transition-colors flex-1 lg:flex-none ${
                              activeDocumentTab === 'overview'
                                ? 'theme-primary theme-text-inverse'
                                : 'theme-text-secondary hover:theme-text'
                            }`}
                          >
                            Overview
                          </button>
                          <button
                            onClick={() => setActiveDocumentTab('content')}
                            className={`px-3 py-2 text-xs rounded-md transition-colors flex-1 lg:flex-none ${
                              activeDocumentTab === 'content'
                                ? 'theme-primary theme-text-inverse'
                                : 'theme-text-secondary hover:theme-text'
                            }`}
                          >
                            Full Content
                          </button>
                        </div>
                        
                        {/* Tab Content */}
                        <div className="max-h-48 lg:max-h-64 overflow-y-auto">
                          {activeDocumentTab === 'overview' ? (
                            <div className="space-y-1">
                              {message.documents.map((doc, index) => (
                                <div 
                                  key={index} 
                                  className={`flex items-start gap-2 p-2 theme-bg-primary rounded-md border theme-border hover:theme-bg-secondary transition-colors ${
                                    doc.file || doc.source_url ? 'cursor-pointer' : ''
                                  }`}
                                  onClick={() => handleDocumentClick(doc)}
                                >
                                  <span className="inline-flex items-center justify-center w-5 h-5 text-center theme-bg-tertiary theme-text rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                                    {index + 1}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium theme-text truncate mb-0.5">
                                      {doc.headline || doc.document}
                                      {doc.file && (
                                        <span className="ml-1 text-xs theme-text-muted">(PDF)</span>
                                      )}
                                    </div>
                                    <div className="text-xs theme-text-secondary flex items-center gap-2">
                                      <span className="truncate">{doc.source}</span>
                                      <span className="text-xs opacity-75">•</span>
                                      <span className="truncate">{doc.jurisdiction}</span>
                                      {(doc.source_url || doc.file) && (
                                        <span className="theme-primary-text flex-shrink-0">
                                          ↗
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {message.documents.map((doc, index) => (
                                <div key={index} className="border-b theme-border pb-3 last:border-b-0">
                                  <div className="text-xs font-medium theme-text mb-1">{doc.document}</div>
                                  <div className="text-xs theme-text-secondary mb-2">
                                    <span className="inline-block mr-2">{doc.category}</span>
                                    {doc.stage && <span className="inline-block mr-2">Stage: {doc.stage}</span>}
                                    {doc.step_order && <span className="inline-block mr-2">Step: {doc.step_order}</span>}
                                    <span className="inline-block mr-2">Jurisdiction: {doc.jurisdiction}</span>
                                    <span className="inline-block">Date: {doc.date}</span>
                                  </div>
                                  <div className="text-xs theme-text-secondary leading-relaxed">
                                    <MarkdownRenderer>{doc.content}</MarkdownRenderer>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-start">
              <div className="flex-1 px-4 lg:px-5 py-2 whitespace-pre-wrap">
                {message.content}
              </div>
              {/* <div className="w-8 h-8 rounded-full bg-[#9ca3af] flex items-center justify-center mr-1 mt-1 shadow-sm flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div> */}
            </div>
          )}
        </div>
      </div>
      
      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfModal.isOpen}
        onClose={closePDFModal}
        pdfUrl={pdfModal.url}
        documentTitle={pdfModal.title}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.citationProcessingEnabled === nextProps.citationProcessingEnabled &&
    JSON.stringify(prevProps.message.documents) === JSON.stringify(nextProps.message.documents)
  );
});
